/* eslint-disable no-param-reassign */
type AnyInputElement = HTMLInputElement | HTMLTextAreaElement;

/**
 * Inserts `text` at `position` and keeps selection intact. Assumes
 * uni-directional update: caller of `insertTextAt()` must take
 * care of updating the `value` prop.
 */
export function insertTextAt(element: AnyInputElement, position: number, text: string) {
  let { selectionStart, selectionEnd } = element;

  const before = element.value.substring(0, position);
  const after  = element.value.substring(position);

  element.value = `${before}${text}${after}`;

  if (typeof selectionStart === 'number' && typeof selectionEnd === 'number') {
    if (selectionStart > position) { selectionStart += text.length; }
    if (selectionEnd > position) { selectionEnd += text.length; }
    element.setSelectionRange(selectionStart, selectionEnd);
  }
}

export function insertTextAtCursor(element: AnyInputElement, text: string) {
  const currentValue = element.value;

  const selectionStart = (
    typeof element.selectionStart === 'number' ?
      element.selectionStart :
      // Default to the end of the text if `selectionStart` isn't available.
      currentValue.length
  );
  const selectionEnd = (
    typeof element.selectionEnd === 'number' ?
      element.selectionEnd :
      // Default to no selection range if `selectionEnd` isn't available.
      selectionStart
  );

  // Insert new text, replacing currently selected text.
  const newValue = (
    currentValue.substring(0, selectionStart) +
    text +
    currentValue.substring(selectionEnd)
  );

  // Update the DOM directly so we can call `setSelectionRange` immediately without having to
  // wait for `componentDidUpdate`.
  element.value = newValue;

  if (element.setSelectionRange) {
    const newCursorPosition = selectionStart + text.length;
    element.setSelectionRange(newCursorPosition, newCursorPosition);
  }
}

/**
 * Deletes `length` amount of characters, starting at
 * `position`. Assumes uni-directional update: caller of
 * `deleteText()` must take care of updating the `value` prop.
 */
export function deleteText(element: AnyInputElement, position: number, length: number) {
  let { selectionStart, selectionEnd } = element;

  const before = element.value.substring(0, position);
  const after  = element.value.substring(position + length);

  element.value = `${before}${after}`;

  if (typeof selectionStart === 'number' && typeof selectionEnd === 'number') {
    if (selectionStart > position) { selectionStart -= length; }
    if (selectionEnd > position) { selectionEnd -= length; }
    element.setSelectionRange(selectionStart, selectionEnd);
  }
}

/**
 * Like HTML autoFocus attribute, but make sure cursor starts at end of text (instead of at the
 * beginning, which is the default browser behavior).
 */
export function autoFocusAtEnd(element: AnyInputElement) {
  if (element) {
    element.focus();

    if (element.value) {
      const lastIndex = element.value.length;
      element.setSelectionRange(lastIndex, lastIndex);
    }
  }
}
